import { LiquidationAsset, TargetCoinLiquidation } from './types'
import { SupportedNetWork } from './constants'
import {
  getTokenBalance,
  getNativeBalance,
  paraController,
  getBestPrice,
  getSourceDestTokenByNetwork,
} from './swapUtils'
import { formatBigNumberToAmount } from './helper'
import { chunk } from 'lodash'
import Web3 from 'web3'
import Web3HttpProvider from 'web3-providers-http'

// Interface for individual chain status
export interface ChainStatus {
  chainId: number
  chainName: string
  status: 'pending' | 'loading' | 'completed' | 'error'
  tokenCount: number
  error?: string
}

// Interface for wallet token state - similar to AvailableCoinListState
export interface WalletTokenState {
  tokenByNetwork: Record<string, LiquidationAsset[]>
  tokensList: LiquidationAsset[]
  isLoading: boolean
  error?: string
  chainStatuses: ChainStatus[]
  completedChains: number
  totalChains: number
}

/**
 * Fetch tokens for a specific chain - exactly like SellAllRedeploy approach
 */
export const fetchTokensForChain = async (
  chainId: number,
  userAddress: string,
): Promise<LiquidationAsset[]> => {
  try {
    const network = SupportedNetWork.find((n) => n.chainId === chainId)
    if (!network) {
      throw new Error(`Network with chainId ${chainId} not found`)
    }

    // Setup paraController for this network first
    paraController.onChangeNetwork(network)

    const tokens: LiquidationAsset[] = []

    // Get native token balance - like SellAllRedeploy
    const nativeBalance = await getNativeBalance(userAddress)
    if (nativeBalance > 0) {
      // Prepare a native token object first
      const nativeTokenBase: LiquidationAsset = {
        address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
        symbol: network.symbol,
        name: network.name,
        decimals: network.decimals,
        balance: nativeBalance.toString(),
        balanceString: nativeBalance.toString(),
        USDBalance: 0,
        stringUSDBalance: '0',
        contract_address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
        cmcCoinId: network.cmcCoinId,
        isCustom: false,
        stringBalance: nativeBalance,
        network: network,
        nativeCurrency: network.nativeCurrency,
        isSelected: false,
        loading: false,
        allocation: 0,
        price: '',
        error: '',
        isNative: true,
      }

      // Try to compute USD value using ParaSwap rate to USDT
      try {
        const { inputToken, outputToken } = await getSourceDestTokenByNetwork(
          network.chainId,
          nativeTokenBase,
          TargetCoinLiquidation.USDC,
        )

        if (outputToken) {
          const { priceRoute } = await getBestPrice({
            amount: nativeBalance.toString(),
            inputToken: inputToken as any,
            outputToken: outputToken as any,
            userAddress: '0x0000000000000000000000000000000000000000',
            onHandleError: () => {},
          })

          const usd = Number((priceRoute as any)?.srcUSD || 0)
          const nativeToken: LiquidationAsset = {
            ...nativeTokenBase,
            USDBalance: usd,
            stringUSDBalance: usd.toString(),
          }
          tokens.push(nativeToken)
        } else {
          tokens.push(nativeTokenBase)
        }
      } catch (e) {
        tokens.push(nativeTokenBase)
      }
    }

    // Fetch ERC20 tokens using RPC batching (getTokensBalanceByNetwork)
    try {
      // Check if ParaSwap supports this chain based on current SupportedNetWork list
      const supportedChains = SupportedNetWork.map((n: any) => n.chainId)
      if (!supportedChains.includes(network.chainId)) {
        return tokens
      }

      const tokensOrError = await paraController.paraSwap.getTokens()

      if (tokensOrError?.message) {
        // Continue without ERC20 tokens for this chain
        return tokens
      }

      if (Array.isArray(tokensOrError)) {
        // Limit to first 20 tokens to avoid rate limits
        const limitedTokens = tokensOrError.filter(
          (t: any) =>
            t.address?.toLowerCase() !== '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' &&
            t.symbol?.toLowerCase() !== 'usdt' &&
            t.symbol?.toLowerCase() !== 'usdc',
        )

        // Prepare input for batch balance fetch
        const inputTokensForBatch = limitedTokens.map((t: any) => ({
          contract_address: t.address,
          symbol: t.symbol,
          name: t.name,
          decimals: t.decimals,
        }))

        const batchBalances: any[] = await getTokensBalanceByNetworkMulticall(
          inputTokensForBatch,
          network,
          userAddress,
        )
        for (const item of batchBalances) {
          const tokenMeta = limitedTokens.find((t: any) => t.address === item.contract_address)
          if (!tokenMeta) continue

          const balance = item.balance
          if (Number(balance) > 0) {
            const balanceFormatted = formatBigNumberToAmount(balance, tokenMeta.decimals)
            const erc20TokenBase: LiquidationAsset = {
              address: tokenMeta.address,
              symbol: tokenMeta.symbol,
              name: tokenMeta.name,
              decimals: tokenMeta.decimals,
              balance: balance,
              balanceString: balanceFormatted.toString(),
              USDBalance: 0,
              stringUSDBalance: '0',
              contract_address: tokenMeta.address,
              cmcCoinId: undefined,
              isCustom: false,
              stringBalance: balanceFormatted.toString(),
              network: network,
              nativeCurrency: undefined,
              isSelected: false,
              loading: false,
              allocation: 0,
              price: '',
              error: '',
              isNative: false,
            }

            // Try to compute USD value using ParaSwap rate to USDT
            try {
              const { inputToken, outputToken } = await getSourceDestTokenByNetwork(
                network.chainId,
                erc20TokenBase,
                TargetCoinLiquidation.USDC,
              )

              if (outputToken) {
                const { priceRoute } = await getBestPrice({
                  amount: balanceFormatted.toString(),
                  inputToken: inputToken as any,
                  outputToken: outputToken as any,
                  userAddress: '0x0000000000000000000000000000000000000000',
                  onHandleError: () => {},
                })

                const usd = Number((priceRoute as any)?.srcUSD || 0)
                const erc20Token: LiquidationAsset = {
                  ...erc20TokenBase,
                  USDBalance: usd,
                  stringUSDBalance: usd.toString(),
                }
                tokens.push(erc20Token)
              } else {
                tokens.push(erc20TokenBase)
              }
            } catch (e) {
              tokens.push(erc20TokenBase)
            }
          }
        }
      }
    } catch (e) {
      // Continue with native token only
    }

    return tokens
  } catch (error) {
    return []
  }
}

/**
 * Build data structure exactly like SellAllRedeploy - for custom liquidation
 */
export const buildCustomLiquidationData = (
  walletTokenState: WalletTokenState,
): Record<string, unknown> => {
  const customData: Record<string, unknown> = {}

  Object.entries(walletTokenState.tokenByNetwork).forEach(([chainId, chainTokens]) => {
    const tokens = (chainTokens ?? [])
      .filter((e: LiquidationAsset) => Number(e.USDBalance) > 0)
      .sort((a: LiquidationAsset, b: LiquidationAsset) => a.symbol.localeCompare(b.symbol))

    if (tokens.length > 0) {
      const networkName =
        tokens[0]?.network?.name ||
        SupportedNetWork.find((network) => network.chainId.toString() === chainId)?.name ||
        chainId

      customData[chainId] = {
        name: networkName,
        isSelected: false,
        ...tokens.reduce(
          (acc, token) => {
            acc[token.symbol] = {
              ...token,
              isSelected: false,
              loading: false,
              allocation: 0,
              price: token.USDBalance.toString(),
              error: '',
            }
            return acc
          },
          {} as Record<string, unknown>,
        ),
      }
    }
  })

  return customData
}

/**
 * Fetch all tokens for all supported chains with progress tracking
 */
export const fetchAllWalletTokens = async (
  userAddress: string,
  onProgress?: (state: WalletTokenState) => void,
): Promise<WalletTokenState> => {
  const tokenByNetwork: Record<string, LiquidationAsset[]> = {}
  const tokensList: LiquidationAsset[] = []
  let isLoading = true
  let error: string | undefined
  let completedChains = 0
  const totalChains = SupportedNetWork.length

  // Initialize chain statuses
  const chainStatuses: ChainStatus[] = SupportedNetWork.map((network) => ({
    chainId: network.chainId,
    chainName: network.name,
    status: 'pending' as const,
    tokenCount: 0,
  }))

  const updateProgress = () => {
    const currentState: WalletTokenState = {
      tokenByNetwork: { ...tokenByNetwork },
      tokensList: [...tokensList],
      isLoading,
      error,
      chainStatuses: [...chainStatuses],
      completedChains,
      totalChains,
    }
    onProgress?.(currentState)
  }

  try {
    // Fetch tokens for each supported network
    for (let i = 0; i < SupportedNetWork.length; i++) {
      const network = SupportedNetWork[i]

      try {
        // Update status to loading
        chainStatuses[i].status = 'loading'
        updateProgress()

        const tokens = await fetchTokensForChain(network.chainId, userAddress)

        tokenByNetwork[network.chainId.toString()] = tokens
        tokensList.push(...tokens)
        completedChains++

        // Update status to completed
        chainStatuses[i].status = 'completed'
        chainStatuses[i].tokenCount = tokens.length
        updateProgress()
      } catch (err) {
        tokenByNetwork[network.chainId.toString()] = []
        completedChains++

        // Update status to error
        chainStatuses[i].status = 'error'
        chainStatuses[i].error = err instanceof Error ? err.message : 'Unknown error'
        updateProgress()

        // Continue with other networks instead of failing completely
      }
    }

    isLoading = false
    updateProgress()
  } catch (err) {
    error = err instanceof Error ? err.message : 'Unknown error'
    isLoading = false
    updateProgress()
  }

  return {
    tokenByNetwork,
    tokensList,
    isLoading,
    error,
    chainStatuses,
    completedChains,
    totalChains,
  }
}

/**
 * Get tokens for a specific network - similar to SellAllRedeploy usage
 */
export const getTokensByNetwork = (
  walletState: WalletTokenState,
  chainId: number,
): LiquidationAsset[] => {
  return walletState.tokenByNetwork[chainId.toString()] || []
}

/**
 * Refetch balance for specific tokens after swap
 */
export const refetchTokenBalances = async (
  tokens: LiquidationAsset[],
  userAddress: string,
  onProgress?: (updatedTokens: LiquidationAsset[]) => void,
): Promise<LiquidationAsset[]> => {
  const updatedTokens: LiquidationAsset[] = []

  for (const token of tokens) {
    try {
      // Set loading state
      const loadingToken = { ...token, loading: true }
      onProgress?.([...updatedTokens, loadingToken])

      // Get fresh balance
      const { balance } = await getTokenBalance({
        userAddress,
        token: {
          address: token.address,
          symbol: token.symbol,
          name: token.name,
          decimals: token.decimals,
          network: token.network,
        } as LiquidationAsset,
      })

      if (Number(balance) > 0) {
        const balanceFormatted = formatBigNumberToAmount(balance, token.decimals)
        const updatedToken: LiquidationAsset = {
          ...token,
          balance: balance,
          balanceString: balanceFormatted.toString(),
          stringBalance: balanceFormatted.toString(),
          loading: false,
        }
        updatedTokens.push(updatedToken)
      }

      onProgress?.([...updatedTokens])
    } catch (error) {
      // Keep original token if refetch fails
      updatedTokens.push({ ...token, loading: false })
    }
  }

  return updatedTokens
}

const getTokenCountPerRequestByNetwork = (networkName: string) => {
  switch (networkName) {
    case 'Avalanche':
      return 49
    case 'Ethereum':
      return 500
    case 'Polygon':
      return 50
    case 'Arbitrum':
      return 25
    default:
      return 50
  }
}

const minABI: any = [
  // balanceOf
  {
    inputs: [{ name: '_owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: 'balance', type: 'uint256' }],
    type: 'function',
  },
  // decimals
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ name: 'decimals', type: 'uint8' }],
    type: 'function',
  },
]

// Multicall3 ABI (tryAggregate)
const multicallAbi: any = [
  {
    inputs: [
      { name: 'requireSuccess', type: 'bool' },
      {
        components: [
          { name: 'target', type: 'address' },
          { name: 'callData', type: 'bytes' },
        ],
        name: 'calls',
        type: 'tuple[]',
      },
    ],
    name: 'tryAggregate',
    outputs: [
      {
        components: [
          { name: 'success', type: 'bool' },
          { name: 'returnData', type: 'bytes' },
        ],
        name: 'returnData',
        type: 'tuple[]',
      },
    ],
    stateMutability: 'nonpayable',
    type: 'function',
  },
]

// Common Multicall3 address on many chains
const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11'

const getTokensBalanceByNetworkMulticall = async (tokens: any[], network: any, account: string) => {
  if (!tokens.length) return []
  const web3 = new Web3()
  web3.setProvider(new Web3HttpProvider(network?.rpc))

  const chunkCount = getTokenCountPerRequestByNetwork(network.name)
  const tokenChunks = chunk(tokens, chunkCount)
  const responses: any[] = []

  for (const tokenChunk of tokenChunks) {
    const multicall = new web3.eth.Contract(multicallAbi, MULTICALL3_ADDRESS)
    const erc20 = new web3.eth.Contract(minABI)

    const calls = tokenChunk.map((t: any) => ({
      target: t.contract_address,
      callData: erc20.methods.balanceOf(account).encodeABI(),
    }))

    try {
      const result: any[] = await multicall.methods.tryAggregate(false, calls).call()

      result.forEach((ret, idx) => {
        const success = ret.success
        const returnData = ret.returnData as string
        let balance = '0'
        if (success && returnData && returnData !== '0x') {
          const decoded = web3.eth.abi.decodeParameter('uint256', returnData)
          balance = decoded?.toString?.() || '0'
        }
        responses.push({
          ...tokenChunk[idx],
          balance,
        })
      })
    } catch (e) {
      tokenChunk.forEach((t: any) => {
        responses.push({ ...t, balance: '0' })
      })
    }

    // brief delay between chunks
    await new Promise((r) => setTimeout(r, 200))
  }

  return responses
}
